import { createContext, useContext, useMemo, type ReactNode } from 'react';
import type { Features, FeatureAccessState } from './types';
import { evaluateFeatureAccess } from './utils';

const FeatureFlagContext = createContext<FeatureAccessState | null>(null);

export interface FeatureFlagProviderProps {
  otherInfo: Record<string, unknown> | null | undefined;
  isTenantReady: boolean;
  children: ReactNode;
}

export const FeatureFlagProvider = ({
  otherInfo,
  isTenantReady,
  children,
}: FeatureFlagProviderProps) => {
  const value = useMemo<FeatureAccessState>(
    () => ({
      isTenantReady,
      access: evaluateFeatureAccess(otherInfo),
    }),
    [otherInfo, isTenantReady]
  );
  return (
    <FeatureFlagContext.Provider value={value}>
      {children}
    </FeatureFlagContext.Provider>
  );
};

export interface UseFeatureFlagResult {
  isTenantReady: boolean;
  isEnabled: boolean;
}

export const useFeatureFlag = (featureKey: Features): UseFeatureFlagResult => {
  const state = useContext(FeatureFlagContext);
  if (!state) {
    throw new Error(
      'useFeatureFlag must be used within a <FeatureFlagProvider>'
    );
  }
  return {
    isTenantReady: state.isTenantReady,
    isEnabled: !!state.access[featureKey],
  };
};

export interface FeatureFlagProps {
  featureKey: Features;
  children: ReactNode;
  fallback?: ReactNode;
}

export const FeatureFlag = ({
  featureKey,
  children,
  fallback = null,
}: FeatureFlagProps) => {
  const { isTenantReady, isEnabled } = useFeatureFlag(featureKey);
  if (!isTenantReady) return null;
  return <>{isEnabled ? children : fallback}</>;
};
